import React, { CSSProperties, ReactNode, useEffect, useMemo, useState } from 'react';
import { ProjectSortInput, Tag, UserRoleEnum, useGetAllProjectsQuery, useGetDefaultQuery, useGetProjectsSummaryQuery } from '../../generated/graphql';
import { useCurrentUser, useIsExternalUser } from '../../App.hooks';
import DeleteProjectsDialog from '../../components/dialogs/DeleteProjectsDialog/DeleteProjectsDialog';
import ShowOnRole from '../../components/main/ShowOnRole/ShowOnRole';
import RequestAccessProjectDialog from '../../components/dialogs/RequestAccessProjectDialog/RequestAccessProjectDialog';
import AddProjectsTagsDialog from '../../components/dialogs/AddProjectsTagsDialog/AddProjectsTagsDialog';
import AddProjectHealthChecksDialog from '../../components/dialogs/AddProjectHealthChecksDialog/AddProjectHealthChecksDialog';
import DataTableNew from '../../components/main/DataTableNew/DataTableNew';
import { KeyRound, Tags, Activity, Trash2, Building2, EyeOff, Eye, ArrowUp, ArrowDown } from 'lucide-react';
import RefreshButton from '../../components/main/RefreshButton/RefreshButton';
import clsx from 'clsx';
import { createGraphQLOrder, TableSort } from '../../components/main/DataTableNew/DataTableSorting';

type ProjectsProps = {
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
};

const Projects = (props: ProjectsProps) => {
    const defaultResult = useGetDefaultQuery();
    const isProjSubNameEnabled = defaultResult?.data?.Default?.isProjSubNameEnabled;
    const [toggle, setToggle] = useState(false);

    useEffect(() => {
        setToggle(isProjSubNameEnabled !== undefined ? isProjSubNameEnabled : false);
    }, [isProjSubNameEnabled]);

    const { style } = props;
    const [searchText, setSearchText] = useState("");
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [deleteProjectsDialogVisible, setDeleteProjectsDialogVisible] = useState(false);
    const [assignProjectsTagsVisible, setAssignProjectsTagsVisible] = useState(false);
    const [requestAccessVisible, setRequestAccessVisible] = useState(false);
    const [assignHealthCheckVisible, setAssignHealthCheckVisible] = useState(false);
    const [tagIds, setTagIds] = useState<string[]>([]);
    const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
    const [revealedClientIds, setRevealedClientIds] = useState<Set<string>>(new Set());
    const [sort, setSort] = useState<TableSort | null>({ field: 'createdTime', direction: 'desc' });

    const result = useGetAllProjectsQuery({
        variables: {
            tagIds: tagIds,
            order: createGraphQLOrder<ProjectSortInput>(sort, { field: 'createdTime', direction: 'desc' }),
            where: {
                name: searchText ? { contains: searchText } : undefined,
                clientId: selectedClientId ? { eq: selectedClientId } : undefined,
            },
        }
    });

    // Lightweight query that fetches ALL projects (up to 10 000) for accurate card totals
    const summaryResult = useGetProjectsSummaryQuery({
        variables: { tagIds: tagIds }
    });

    const handleOnClose = () => {
        setRequestAccessVisible(false);
        setDeleteProjectsDialogVisible(false);
        setAssignProjectsTagsVisible(false);
        setAssignHealthCheckVisible(false);
        setSelectedIds([]);
        result.refetch();
        summaryResult.refetch();
    };

    const handleFilterChanged = (tags: Tag[]) => {
        tags && setTagIds(tags.map(t => t.id || ""));
    };

    const currentUser = useCurrentUser();
    const isExternalUser = useIsExternalUser();
    const isGlobalAdmin = currentUser?.role === UserRoleEnum.Admin;

    // Derive top 5 clients by project count from the summary query (all projects)
    // Exclude private clients unless the user is a global admin or a client admin
    const topClients = useMemo(() => {
        const nodes = summaryResult.data?.ProjectList?.nodes;
        if (!nodes) return [];
        const counts = new Map<string, { id: string; name: string; count: number; isPrivate: boolean }>();
        for (const project of nodes) {
            const client = project?.client;
            const clientName = client?.name;
            const clientId = client?.id ?? project?.clientId;
            if (!clientName || !clientId) continue;
            if (client?.isPrivate === true && !isGlobalAdmin && !client?.isAdmin) continue;
            const existing = counts.get(clientId);
            if (existing) {
                existing.count++;
            } else {
                counts.set(clientId, { id: clientId, name: clientName, count: 1, isPrivate: client?.isPrivate === true });
            }
        }
        return Array.from(counts.values())
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
    }, [summaryResult.data, isGlobalAdmin]);

    const totalProjects = summaryResult.data?.ProjectList?.nodes?.filter(
        (p: any) => !(p?.client?.isPrivate === true && !isGlobalAdmin && !p?.client?.isAdmin)
    )?.length ?? 0;

    const cardColors = [
        { color: 'var(--accent)', bgColor: 'var(--accent-soft)' },
        { color: 'var(--success)', bgColor: 'var(--success-soft)' },
        { color: 'var(--info)', bgColor: 'var(--info-soft)' },
        { color: 'var(--warning)', bgColor: 'var(--warning-soft)' },
        { color: '#8b5cf6', bgColor: '#f5f3ff' },
    ];

    const customColumns = [
        ...(toggle ? [{ id: "projectSubName", name: "Sub Name", sortField: 'projectSubName' }] : []),
        { id: "client.name", name: "Client" },
        { id: "library.name", name: "Library" },
        {
            id: "healthChecks",
            name: "Health Checks",
            width: "160px",
            render: (row: any) => (
                <div className="flex flex-wrap gap-1">
                    {row.healthChecks?.filter((i: any) => i).map((g: any) => (
                        <span key={g.id || g.name} className="badge-success">{g.name}</span>
                    ))}
                </div>
            ),
        },
    ];

    return (
        <>
            <div className="page-container" style={style} data-testid="Projects">
                {/* Header */}
                <div className="page-header">
                    <div>
                        <h1 className="page-title">Projects</h1>
                        <p className="page-subtitle">Browse and manage project content and models</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <RefreshButton onRefresh={() => result.refetch()} className="btn-ghost" iconSize={16} showLabel={false} />
                        <button
                            className="btn-secondary"
                            title={sort?.field === 'createdTime' && sort.direction === 'asc' ? 'Oldest first' : 'Newest first'}
                            onClick={() => setSort({ field: 'createdTime', direction: sort?.field === 'createdTime' && sort.direction === 'desc' ? 'asc' : 'desc' })}
                        >
                            {sort?.field === 'createdTime' && sort.direction === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                            {sort?.field === 'createdTime' && sort.direction === 'asc' ? 'Oldest first' : 'Newest first'}
                        </button>
                        {!isExternalUser && (
                            <button className="btn-secondary" onClick={() => setRequestAccessVisible(true)}>
                                <KeyRound size={16} />
                                Request Access
                            </button>
                        )}

                        {/* Sub-name toggle */}
                        <label className="flex items-center gap-2 text-sm cursor-pointer select-none px-3 py-2 rounded-lg" style={{ color: 'var(--text-secondary)' }}>
                            <span>Sub-name</span>
                            <div
                                className={clsx('relative w-9 h-5 rounded-full transition-colors duration-200 cursor-pointer')}
                                style={{ backgroundColor: toggle ? 'var(--accent)' : 'var(--surface-300)' }}
                                onClick={() => setToggle(!toggle)}
                            >
                                <div
                                    className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform duration-200"
                                    style={{ transform: toggle ? 'translateX(18px)' : 'translateX(2px)', boxShadow: 'var(--shadow-sm)' }}
                                />
                            </div>
                        </label>

                        <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={false}>
                            {selectedIds.length > 0 && (
                                <>
                                    <button className="btn-secondary" onClick={() => setAssignProjectsTagsVisible(true)}>
                                        <Tags size={16} /> Tags
                                    </button>
                                    <button className="btn-secondary" onClick={() => setAssignHealthCheckVisible(true)}>
                                        <Activity size={16} /> Health Checks
                                    </button>
                                    <button className="btn-danger" onClick={() => setDeleteProjectsDialogVisible(true)}>
                                        <Trash2 size={16} /> Delete ({selectedIds.length})
                                    </button>
                                </>
                            )}
                        </ShowOnRole>
                    </div>
                </div>

                {/* Top 5 Clients by Project Count */}
                {topClients.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 animate-slide-up">
                        {topClients.map((client, index) => {
                            const palette = cardColors[index % cardColors.length];
                            const pct = totalProjects > 0 ? Math.round((client.count / totalProjects) * 100) : 0;
                            const isActive = selectedClientId === client.id;
                            const isRevealed = !client.isPrivate || revealedClientIds.has(client.id);
                            return (
                                <div
                                    key={client.id}
                                    className={clsx('card p-5 group cursor-pointer transition-all', isActive && 'ring-2')}
                                    style={isActive ? { borderColor: palette.color, boxShadow: `0 0 0 2px ${palette.color}` } : {}}
                                    onClick={() => isRevealed && setSelectedClientId(prev => prev === client.id ? null : client.id)}
                                >
                                    <div className="flex items-center justify-between mb-4">
                                        <div
                                            className="w-10 h-10 rounded-xl flex items-center justify-center"
                                            style={{ backgroundColor: palette.bgColor }}
                                        >
                                            <Building2 size={20} style={{ color: palette.color }} />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {client.isPrivate && (
                                                <button
                                                    className="p-1 rounded-md transition-colors"
                                                    style={{ color: 'var(--text-tertiary)', border: 'none', background: 'none', cursor: 'pointer' }}
                                                    title={isRevealed ? 'Hide client name' : 'Reveal client name'}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setRevealedClientIds(prev => {
                                                            const next = new Set(prev);
                                                            if (next.has(client.id)) next.delete(client.id);
                                                            else next.add(client.id);
                                                            return next;
                                                        });
                                                    }}
                                                >
                                                    {isRevealed ? <Eye size={14} /> : <EyeOff size={14} />}
                                                </button>
                                            )}
                                            <span
                                                className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
                                                style={{ backgroundColor: palette.bgColor, color: palette.color }}
                                            >
                                                {pct}%
                                            </span>
                                        </div>
                                    </div>
                                    <div className="stat-value">{client.count}</div>
                                    <div className="stat-label mt-1 truncate" title={isRevealed ? client.name : 'Private client'}>
                                        {isRevealed ? client.name : (
                                            <span style={{ filter: 'blur(5px)', userSelect: 'none' }}>{client.name}</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Data Table */}
                <div className="card flex flex-col flex-1 min-h-0 overflow-hidden">
                    <DataTableNew
                        sortingMode="server"
                        sort={sort}
                        onSortChange={setSort}
                        isLoading={result.loading}
                        data={result.data?.ProjectList?.nodes}
                        link="/projects"
                        showTags
                        columns={customColumns}
                        onSearchChanged={setSearchText}
                        onFilterChanged={handleFilterChanged}
                        onSelected={setSelectedIds}
                        fetchMore={() => result.data?.ProjectList?.pageInfo?.hasNextPage && result.fetchMore({
                            variables: {
                                after: result.data?.ProjectList?.pageInfo.endCursor,
                                order: createGraphQLOrder<ProjectSortInput>(sort, { field: 'createdTime', direction: 'desc' }),
                                where: {
                                    name: searchText ? { contains: searchText } : undefined,
                                    clientId: selectedClientId ? { eq: selectedClientId } : undefined,
                                },
                            },
                            updateQuery: (previousResult, { fetchMoreResult }) => {
                                if (!fetchMoreResult) return previousResult;
                                const previousContents = result.data?.ProjectList?.nodes || [];
                                const newContents = fetchMoreResult.ProjectList?.nodes || [];
                                return {
                                    ...previousResult,
                                    ProjectList: {
                                        nodes: [...previousContents, ...newContents],
                                        pageInfo: fetchMoreResult.ProjectList?.pageInfo as any
                                    }
                                };
                            }
                        })}
                    />
                </div>
            </div>
            {deleteProjectsDialogVisible && <DeleteProjectsDialog ids={selectedIds} visible={deleteProjectsDialogVisible} onClose={handleOnClose} />}
            {requestAccessVisible && <RequestAccessProjectDialog visible={requestAccessVisible} onClose={handleOnClose} />}
            {assignProjectsTagsVisible && <AddProjectsTagsDialog ids={selectedIds} visible={assignProjectsTagsVisible} onClose={handleOnClose} />}
            {assignHealthCheckVisible && <AddProjectHealthChecksDialog ids={selectedIds} visible={assignHealthCheckVisible} onClose={handleOnClose} />}
        </>
    );
};

export default Projects;
